import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { setSentryUser } from '../config/sentry';
import { logger } from '../utils/logger';
import type { UserRole } from '../types';
import { normalizeRole } from '../utils/role';

interface AuthContextType {
  currentUser: User | null;
  /**
   * Role derived from the Firebase Auth custom claim, normalized to
   * the canonical trio (`'admin' | 'power' | 'user'`). A stored claim
   * of `'staff'` (pre-migration legacy) is normalized to `'user'` on
   * read, so every consumer sees the post-rename naming. New roles
   * are written by the setUserRole Cloud Function in canonical form.
   */
  role: UserRole | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether we've already stamped users/{uid}.lastLoginAt for
  // the current session. Reset on sign-out so the next sign-in stamps
  // again. Prevents redundant writes on every token refresh.
  const stampedLoginRef = useRef(false);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  // Signup is deliberately NOT exposed. Accounts are created only by the
  // `inviteUser` Cloud Function (admin-only). Even if a malicious actor
  // calls `createUserWithEmailAndPassword` directly via the SDK, the
  // `onUserCreated` trigger deletes any user that wasn't invited.

  const logout = async () => {
    await signOut(auth);
  };

  useEffect(() => {
    let mounted = true;
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!mounted) return;
      setCurrentUser(user);
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          if (!mounted) return;
          // Normalize the raw claim to the canonical trio. Legacy
          // 'staff' claims read as 'user' until the one-time migration
          // converges every stored value (see src/utils/role.ts).
          const resolvedRole = normalizeRole(
            tokenResult.claims.role as string | undefined,
          );
          setRole(resolvedRole);
          setSentryUser({
            uid: user.uid,
            email: user.email,
            role: resolvedRole ?? undefined,
          });
        } catch {
          if (mounted) setRole(null);
        }

        // Stamp users/{uid}.lastLoginAt once per session. Fire-and-
        // forget — a Firestore failure shouldn't block sign-in. The
        // firestore.rules entry allows self-update of this one field.
        if (!stampedLoginRef.current) {
          stampedLoginRef.current = true;
          setDoc(
            doc(db, 'users', user.uid),
            { lastLoginAt: serverTimestamp() },
            { merge: true },
          ).catch((err) => {
            logger.warn('[auth] failed to stamp lastLoginAt', err);
          });
        }
      } else {
        setRole(null);
        setSentryUser(null);
        stampedLoginRef.current = false;
      }
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Mid-session lockout detection. When an admin sets disabled=true
  // via the setUserDisabled Cloud Function, the function mirrors the
  // flag to users/{uid}. We listen to that doc for the current user
  // and force-sign-out + redirect with `?locked=1` so the Login
  // screen shows the friendly "your account has been locked" copy
  // instead of the generic "invalid credentials" path the user would
  // otherwise hit after their token TTL expired (~1hr).
  useEffect(() => {
    const user = currentUser;
    if (!user) return;
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        const data = snap.data() as { disabled?: boolean } | undefined;
        if (data?.disabled === true) {
          logger.log('[auth] account locked by admin; signing out');
          // Sign out first; navigate via location swap so React Router
          // doesn't keep the prior tree mounted as we tear down auth.
          signOut(auth).catch(() => {
            /* even if signOut hiccups, force a reload to the locked
               login screen — the SDK will re-check on next request. */
          });
          window.location.assign('/login?locked=1');
        }
      },
      (err) => {
        // Read failure isn't fatal — could be a transient rules quirk
        // or offline blip. Logged for visibility; lockout still gets
        // caught at the next token refresh (~1hr) via the SDK
        // auto-signing the user out on a disabled-account refresh.
        logger.warn('[auth] users/{uid} lockout listener error', err);
      },
    );
    return unsub;
  }, [currentUser]);

  const value = {
    currentUser,
    role,
    login,
    logout,
    loading,
  };

  // Render children unconditionally — consumers (AppContent, ProtectedRoute)
  // branch on `loading` themselves. Gating the whole subtree here caused a
  // flash-of-redirect on initial mount.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
